import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonSpinner,
  IonBadge,
  IonText,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  IonTextarea,
  IonAlert,
  IonBackButton,
  IonButtons,
  IonList,
  IonItem,
} from '@ionic/react';
import {
  checkmarkCircle,
  eyeOutline,
  trashOutline,
  shieldOutline,
} from 'ionicons/icons';
import { useEffect, useState } from 'react';
import {
  getAllReports,
  updateReportStatus,
  getReportStats,
  deleteReport,
  ReportWithProfiles,
  ReportStats,
  isAdmin,
} from '../services/adminService';
import './AdminReports.css';

type StatusFilter = 'all' | 'pending' | 'reviewed' | 'resolved';

const AdminReports: React.FC = () => {
  const [reports, setReports] = useState<ReportWithProfiles[]>([]);
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedReport, setSelectedReport] = useState<ReportWithProfiles | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [showNotesAlert, setShowNotesAlert] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  useEffect(() => {
    checkAdminStatus();
  }, []);

  useEffect(() => {
    if (isUserAdmin) {
      loadData();
    }
  }, [statusFilter, isUserAdmin]);

  const checkAdminStatus = async () => {
    const adminStatus = await isAdmin();
    setIsUserAdmin(adminStatus);
    if (!adminStatus) {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);

    const [reportsResult, statsResult] = await Promise.all([
      getAllReports(statusFilter === 'all' ? undefined : statusFilter),
      getReportStats(),
    ]);

    if (reportsResult.success && reportsResult.reports) {
      setReports(reportsResult.reports);
    }

    if (statsResult.success && statsResult.stats) {
      setStats(statsResult.stats);
    }

    setLoading(false);
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await loadData();
    event.detail.complete();
  };

  const handleUpdateStatus = async (
    reportId: string,
    status: 'pending' | 'reviewed' | 'resolved'
  ) => {
    const result = await updateReportStatus(reportId, status);
    if (result.success) {
      loadData();
    }
  };

  const handleOpenNotes = (report: ReportWithProfiles) => {
    setSelectedReport(report);
    setAdminNotes(report.admin_notes || '');
    setShowNotesAlert(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedReport) return;

    const result = await updateReportStatus(
      selectedReport.id,
      selectedReport.status,
      adminNotes
    );

    if (result.success) {
      setShowNotesAlert(false);
      setSelectedReport(null);
      setAdminNotes('');
      loadData();
    }
  };

  const handleDeleteReport = async () => {
    if (!selectedReport) return;

    const result = await deleteReport(selectedReport.id);
    if (result.success) {
      setShowDeleteAlert(false);
      setSelectedReport(null);
      loadData();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'reviewed':
        return 'primary';
      case 'resolved':
        return 'success';
      default:
        return 'medium';
    }
  };

  if (!isUserAdmin) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/tab3" />
            </IonButtons>
            <IonTitle>Admin Reports</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ textAlign: 'center', marginTop: '50%' }}>
            <IonIcon icon={shieldOutline} style={{ fontSize: '64px', color: 'var(--ion-color-danger)' }} />
            <IonText color="danger">
              <h2>Access Denied</h2>
              <p>You do not have administrator privileges.</p>
            </IonText>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/tab3" />
            </IonButtons>
            <IonTitle>Admin Reports</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ textAlign: 'center', marginTop: '50%' }}>
            <IonSpinner />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/tab3" />
          </IonButtons>
          <IonTitle>Admin Reports</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment
            value={statusFilter}
            onIonChange={(e) => setStatusFilter(e.detail.value as StatusFilter)}
          >
            <IonSegmentButton value="all">
              <IonLabel>
                All
                {stats && <IonBadge color="medium">{stats.total}</IonBadge>}
              </IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="pending">
              <IonLabel>
                Pending
                {stats && stats.pending > 0 && (
                  <IonBadge color="warning">{stats.pending}</IonBadge>
                )}
              </IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="reviewed">
              <IonLabel>
                Reviewed
                {stats && stats.reviewed > 0 && (
                  <IonBadge color="primary">{stats.reviewed}</IonBadge>
                )}
              </IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="resolved">
              <IonLabel>
                Resolved
                {stats && stats.resolved > 0 && (
                  <IonBadge color="success">{stats.resolved}</IonBadge>
                )}
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {/* Stats Card */}
        {stats && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Statistics</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonList>
                <IonItem>
                  <IonLabel>Total Reports</IonLabel>
                  <IonBadge slot="end">{stats.total}</IonBadge>
                </IonItem>
                <IonItem>
                  <IonLabel>Last 24 Hours</IonLabel>
                  <IonBadge slot="end" color="warning">
                    {stats.last24Hours}
                  </IonBadge>
                </IonItem>
                <IonItem>
                  <IonLabel>Pending Review</IonLabel>
                  <IonBadge slot="end" color="warning">
                    {stats.pending}
                  </IonBadge>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>
        )}

        {/* Reports List */}
        <div style={{ padding: '16px' }}>
          {reports.length === 0 ? (
            <IonCard>
              <IonCardContent style={{ textAlign: 'center', padding: '40px' }}>
                <IonText color="medium">
                  <p>No reports to display</p>
                </IonText>
              </IonCardContent>
            </IonCard>
          ) : (
            reports.map((report) => (
              <IonCard key={report.id}>
                <IonCardHeader>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <IonCardTitle style={{ fontSize: '18px' }}>
                        {report.reason}
                      </IonCardTitle>
                      <IonText color="medium" style={{ fontSize: '12px' }}>
                        {formatDate(report.created_at)}
                      </IonText>
                    </div>
                    <IonBadge color={getStatusColor(report.status)}>
                      {report.status}
                    </IonBadge>
                  </div>
                </IonCardHeader>
                <IonCardContent>
                  <div style={{ marginBottom: '12px' }}>
                    <IonText>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        <strong>Reporter:</strong> @{report.reporter?.username || 'Unknown'}
                      </p>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        <strong>Reported User:</strong> @{report.reported_user?.username || 'Unknown'}
                      </p>
                      {report.reported_user?.email && (
                        <p style={{ margin: '4px 0', fontSize: '14px' }}>
                          <strong>Email:</strong> {report.reported_user.email}
                        </p>
                      )}
                    </IonText>
                  </div>

                  {report.description && (
                    <div
                      style={{
                        padding: '12px',
                        background: 'var(--ion-color-light)',
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}
                    >
                      <IonText color="medium" style={{ fontSize: '14px' }}>
                        {report.description}
                      </IonText>
                    </div>
                  )}

                  {report.admin_notes && (
                    <div
                      style={{
                        padding: '12px',
                        background: 'var(--ion-color-primary-tint)',
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}
                    >
                      <IonText style={{ fontSize: '12px' }}>
                        <strong>Admin Notes:</strong>
                      </IonText>
                      <IonText style={{ fontSize: '14px' }}>
                        <p style={{ margin: '4px 0' }}>{report.admin_notes}</p>
                      </IonText>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                    {report.status === 'pending' && (
                      <IonButton
                        size="small"
                        color="primary"
                        onClick={() => handleUpdateStatus(report.id, 'reviewed')}
                      >
                        <IonIcon icon={eyeOutline} slot="start" />
                        Mark Reviewed
                      </IonButton>
                    )}
                    {report.status !== 'resolved' && (
                      <IonButton
                        size="small"
                        color="success"
                        onClick={() => handleUpdateStatus(report.id, 'resolved')}
                      >
                        <IonIcon icon={checkmarkCircle} slot="start" />
                        Mark Resolved
                      </IonButton>
                    )}
                    <IonButton
                      size="small"
                      fill="outline"
                      onClick={() => handleOpenNotes(report)}
                    >
                      {report.admin_notes ? 'Edit Notes' : 'Add Notes'}
                    </IonButton>
                    <IonButton
                      size="small"
                      fill="outline"
                      color="danger"
                      onClick={() => {
                        setSelectedReport(report);
                        setShowDeleteAlert(true);
                      }}
                    >
                      <IonIcon icon={trashOutline} slot="icon-only" />
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>
            ))
          )}
        </div>

        {/* Admin Notes Alert */}
        <IonAlert
          isOpen={showNotesAlert}
          onDidDismiss={() => {
            setShowNotesAlert(false);
            setSelectedReport(null);
            setAdminNotes('');
          }}
          header="Admin Notes"
          message="Add internal notes about this report"
          inputs={[
            {
              name: 'notes',
              type: 'textarea',
              placeholder: 'Enter admin notes...',
              value: adminNotes,
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Save',
              handler: (data) => {
                setAdminNotes(data.notes);
                handleSaveNotes();
              },
            },
          ]}
        />

        {/* Delete Confirmation */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => {
            setShowDeleteAlert(false);
            setSelectedReport(null);
          }}
          header="Delete Report?"
          message="Are you sure you want to permanently delete this report? This action cannot be undone."
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Delete',
              role: 'destructive',
              handler: handleDeleteReport,
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default AdminReports;